// ============================================================
// evidence — a PROVA que o operador lê sem clicar, e a ordem de gravidade
// ============================================================
// O pedido do dono, nas palavras dele: "tem que de fato ser algo que me ajude
// a banir quem merece". Isso tem uma consequência de projeto que a tela de
// Events já mostrou não cumprir:
//
//   O operador não pode ter que ABRIR `dados técnicos` e ler JSON para saber
//   qual arquivo foi adulterado. A prova tem que estar NA LINHA.
//
// Este arquivo extrai, de cada evento, a única frase que decide um ban:
//   • arquivo adulterado + hash esperado × encontrado  (o SSL32 Chile)
//   • nome da DLL injetada                             (wpespy.dll)
//   • nome do processo pego                            (cheatengine-*.exe)
//   • função gráfica desviada + quem desviou           (EndScene / OBS)
//
// ⚠️ AS CHAVES DE PAYLOAD AQUI FORAM LIDAS DO BANCO, NÃO ADIVINHADAS.
// `SUSPICIOUS_MODULE_LOADED` em produção emite `{"module":"wpespy.dll"}` —
// não `dll_name`, não `path`. Escrever `dll_name` deixaria a linha genérica
// justamente no evento que pegou o cheat em campo. (Foi assim que a primeira
// versão do eventHumanizer nasceu errada; ver o comentário de CHAVES_UTEIS lá.)
//
// SEPARAÇÃO DE PAPÉIS com eventHumanizer:
//   eventHumanizer  → O QUE ACONTECEU e QUANTO PESA  (`titulo`, `tom`).
//   evidence        → COM QUE PROVA                  (`linha`, hashes, nomes).
// A gravidade desta tela é derivada do `tom` do humanizer de propósito: ele já
// carrega o invariante `critico ⟺ tier Confirmed no backend`, travado por
// teste de contrato. Inventar uma segunda escala de gravidade aqui criaria uma
// terceira versão da verdade para divergir das outras duas.
// ============================================================

import { humanizarEvento, type TomEvento } from './eventHumanizer';

// ------------------------------------------------------------
// Forma mínima de um evento de evidência.
// ------------------------------------------------------------
// Subconjunto do `Event` de services/events.ts — só o que a prova precisa.
// Tipo próprio para que a fila de ban possa ser alimentada tanto pelo
// endpoint de contas quanto por /admin/events sem conversão.
export interface EventoDeEvidencia {
  id?: string;
  event_type: string;
  payload?: Record<string, unknown> | null;
  severity?: number;
  severity_label?: string;
  attack_category?: string;
  occurrence_count?: number;
  last_seen_at?: string;
  created_at?: string;
  session_uuid?: string | null;
  identity_epoch?: number;
}

export type TipoDeProva =
  | 'hash'       // arquivo do jogo com hash diferente do manifesto
  | 'modulo'     // DLL de terceiro dentro do processo
  | 'processo'   // programa externo pego rodando
  | 'driver'     // driver de kernel na blocklist
  | 'hook'       // função desviada (IAT / D3D9 / prólogo)
  | 'nenhuma';   // o evento não carrega prova nomeável

export interface Prova {
  tipo: TipoDeProva;
  /** Nome do arquivo / DLL / processo / função. Sem caminho absoluto. */
  alvo?: string;
  /** Hash íntegro que o manifesto esperava (completo — a UI trunca). */
  esperado?: string;
  /** Hash encontrado no disco (completo — a UI trunca). */
  encontrado?: string;
  /** Complemento: assinante, padrão que casou, módulo importador. */
  contexto?: string;
  /** A linha única que o operador lê para decidir. Nunca vazia. */
  linha: string;
}

type Payload = Record<string, unknown> | null | undefined;

function txt(p: Payload, ...chaves: string[]): string | undefined {
  if (!p) return undefined;
  for (const k of chaves) {
    const v = p[k];
    if (typeof v === 'string' && v.trim() !== '') return v.trim();
    if (typeof v === 'number' && Number.isFinite(v)) return String(v);
  }
  return undefined;
}

/** Só o nome do arquivo — nunca o caminho (REGRA #10 / LGPD). */
function nomeDeArquivo(v: string | undefined): string | undefined {
  if (!v) return undefined;
  const partes = v.split(/[\\/]/);
  return partes[partes.length - 1] || v;
}

/**
 * Encurta hash para caber na linha, preservando o suficiente para conferir.
 *
 * 12 hex = 48 bits. Não é prova criptográfica de nada e não precisa ser: o
 * hash completo vai no `title` do elemento e no export. O que a linha precisa
 * é que dois hashes DIFERENTES pareçam diferentes de relance — e `1e08da161cf7`
 * × `894a9bfcfe57` cumprem isso já no primeiro caractere.
 */
export function encurtarHash(h: string | undefined, chars = 12): string {
  if (!h) return '—';
  const limpo = h.trim();
  if (limpo.length <= chars) return limpo;
  return `${limpo.slice(0, chars)}…`;
}

/**
 * Extrai a prova de um evento.
 *
 * Nunca lança e nunca devolve `linha` vazia: um payload em formato inesperado
 * cai em `tipo: 'nenhuma'` com a frase do humanizer, que ainda é melhor do que
 * uma célula em branco no meio de uma fila de ban.
 */
export function extrairProva(
  eventType: string,
  payload?: Payload,
): Prova {
  const p = payload ?? undefined;

  switch (eventType) {
    // ---------- Arquivo do jogo adulterado (SSL32 Chile / fmod.dll) ----------
    case 'GAME_FILES_MODIFIED': {
      // `asset_path` já vem RELATIVO do SDK (GameAssetIntegrity.cpp:99) —
      // nomeDeArquivo é cinto de segurança, não conserto.
      const alvo = nomeDeArquivo(txt(p, 'asset_path', 'path', 'file'));
      const esperado = txt(p, 'expected_sha256', 'expected_hash');
      const encontrado = txt(p, 'actual_sha256', 'actual_hash');
      const status = txt(p, 'integrity_status', 'status');

      if (alvo && esperado && encontrado) {
        return {
          tipo: 'hash',
          alvo, esperado, encontrado,
          contexto: status,
          linha: `${alvo} — esperado ${encurtarHash(esperado)} × encontrado ${encurtarHash(encontrado)}`,
        };
      }
      // Frota antiga manda `{method, integrity_status}` e mais nada. Dizer
      // "arquivo adulterado" sem poder dizer QUAL é pior que admitir a
      // limitação — o operador ia procurar um dado que não existe.
      if (alvo) {
        return {
          tipo: 'hash', alvo, contexto: status,
          linha: `${alvo} — hash fora do manifesto (o cliente não reportou os hashes)`,
        };
      }
      return {
        tipo: 'hash', contexto: status,
        linha: 'arquivo do jogo fora do manifesto — cliente antigo não reportou qual',
      };
    }

    // ---------- DLL de terceiro dentro do processo ----------
    case 'SUSPICIOUS_MODULE_LOADED':
    case 'MODULE_SUSPICIOUS':
    case 'MODULE_LOADED_UNVERIFIED':
    case 'DLL_UNLOAD_FAILED':
    case 'DLL_UNLOAD_DONE': {
      // `module` é a chave REAL em produção (PROCESS_DLL_NAME_MATCH).
      const alvo = nomeDeArquivo(txt(p, 'module', 'module_name', 'dll_name', 'module_path', 'path'));
      const assinante = txt(p, 'cert_subject', 'signer_subject', 'owner_signer');
      if (!alvo) break;
      return {
        tipo: 'modulo', alvo, contexto: assinante,
        linha: assinante
          ? `DLL ${alvo} carregada no jogo — assinada por ${assinante}`
          : `DLL ${alvo} carregada no jogo — sem assinatura reconhecida`,
      };
    }

    // ---------- Programa externo ----------
    case 'SUSPICIOUS_PROCESS_DETECTED':
    case 'PROCESS_SUSPICIOUS': {
      const alvo = nomeDeArquivo(txt(p, 'process_name', 'image_name', 'name', 'path'));
      // `matched_pattern` é a chave do payload cru (/admin/events);
      // `match_via` é a que a whitelist da fila de ban exporta. A mesma linha
      // tem que funcionar nas duas origens.
      const padrao = txt(p, 'matched_pattern', 'pattern', 'match_via');
      if (!alvo) break;
      return {
        tipo: 'processo', alvo, contexto: padrao,
        linha: padrao
          ? `processo ${alvo} rodando — casou a regra ${padrao}`
          : `processo ${alvo} rodando junto do jogo`,
      };
    }

    // ⚠️ Os dois casos abaixo NÃO existiam na primeira versão deste arquivo.
    // Foram achados varrendo os 1.145 payloads distintos do banco com o
    // extrator: `OPEN_HANDLE_SUSPECTED` sozinho respondia por 185 payloads
    // distintos que caíam em frase genérica — e ele nomeia o programa que
    // abriu handle para o jogo, que é exatamente o dado de decidir ban.
    // Ambos trazem CAMINHO ABSOLUTO no payload; `nomeDeArquivo` deixa de ser
    // cinto de segurança e passa a ser o que impede o caminho de ir para a tela.
    case 'OPEN_HANDLE_SUSPECTED': {
      const alvo = nomeDeArquivo(txt(p, 'process_name', 'image_name', 'path'));
      if (!alvo) break;
      return {
        tipo: 'processo', alvo, contexto: txt(p, 'method'),
        linha: `${alvo} abriu handle para o processo do jogo`,
      };
    }

    case 'SUSPICIOUS_PARENT_PROCESS': {
      const alvo = nomeDeArquivo(txt(p, 'parent_path', 'parent_name'));
      if (!alvo) break;
      return {
        tipo: 'processo', alvo, contexto: txt(p, 'method'),
        linha: `jogo foi aberto por ${alvo}, e não pelo launcher`,
      };
    }

    // ---------- Driver de kernel ----------
    case 'KERNEL_DRIVER_BLOCKLISTED':
    case 'KERNEL_DRIVER_SUSPECTED': {
      const alvo = nomeDeArquivo(txt(p, 'driver_name', 'driver', 'service_name'));
      if (!alvo) break;
      return {
        tipo: 'driver', alvo,
        linha: `driver ${alvo} carregado no kernel — está na blocklist`,
      };
    }

    // ---------- Função desviada ----------
    case 'D3D9_FOREIGN_HOOK':
    case 'D3D9_INLINE_HOOK': {
      const fn = txt(p, 'vfunc_name');
      const quem = nomeDeArquivo(txt(p, 'module_path', 'dest_module', 'module'));
      const assinante = txt(p, 'signer_subject', 'cert_subject');
      if (!fn && !quem) break;
      return {
        tipo: 'hook', alvo: fn ?? quem, contexto: assinante ?? quem,
        linha: quem
          ? `função gráfica ${fn ?? '(desconhecida)'} desviada por ${quem}${assinante ? ` (${assinante})` : ''}`
          : `função gráfica ${fn} desviada dentro do processo`,
      };
    }

    case 'IAT_TAMPER_SUSPECTED':
    case 'IAT_INLINE_HOOK_SUSPECTED': {
      const fn = txt(p, 'import_function', 'target_function');
      const modulo = nomeDeArquivo(txt(p, 'target_module'));
      const importador = nomeDeArquivo(txt(p, 'importing_module'));
      if (!fn && !modulo) break;
      return {
        tipo: 'hook', alvo: fn ?? modulo, contexto: importador,
        linha: `import ${fn ?? '(?)'} de ${modulo ?? '(?)'} redirecionado${importador ? ` (visto em ${importador})` : ''}`,
      };
    }

    // ---------- Binário do próprio cliente ----------
    case 'CLIENT_INTEGRITY_FAILED':
    case 'CLIENT_BUILD_INVALID': {
      const build = txt(p, 'build_name', 'client_build');
      // ⚠️ DUAS GRAFIAS. O payload do SDK usa `hash_disk`/`hash_memory`
      // (50 eventos cada no banco); a whitelist da fila de ban exporta
      // `disk_hash`/`memory_hash` (0 eventos — ver relatório). Ler as duas é o
      // que mantém a linha viva qualquer que seja a origem.
      const disco = txt(p, 'hash_disk', 'disk_hash', 'current_hash');
      const memoria = txt(p, 'hash_memory', 'memory_hash', 'baseline_hash');
      if (disco && memoria && disco !== memoria) {
        return {
          tipo: 'hash', alvo: build, esperado: disco, encontrado: memoria,
          linha: `build ${build ?? '(?)'} — disco ${encurtarHash(disco)} × memória ${encurtarHash(memoria)}`,
        };
      }
      break;
    }

    default:
      break;
  }

  // Sem prova nomeável: a frase do humanizer é o melhor que existe.
  const h = humanizarEvento(eventType, p);
  return {
    tipo: 'nenhuma',
    linha: h.detalhe ? `${h.titulo} — ${h.detalhe}` : h.titulo,
  };
}

// ============================================================
// GRAVIDADE — a ordem da fila
// ============================================================
// A fila de ban é lida DE CIMA PARA BAIXO e o operador para quando cansa.
// Portanto a ordem não é um detalhe estético: é o que decide quem é olhado.

/** Peso do tom. Espelha PESO_TOM do eventHumanizer (mesma escala, mesma ordem). */
const PESO_TOM: Record<TomEvento, number> = {
  critico: 5,
  forte: 4,
  contexto: 3,
  info: 2,
  operacional: 1,
};

export function pesoDoTom(tom: TomEvento): number {
  return PESO_TOM[tom];
}

/** Pior tom entre as evidências de uma conta. Lista vazia → `operacional`. */
export function piorTom(evidencias: readonly EventoDeEvidencia[]): TomEvento {
  let pior: TomEvento = 'operacional';
  for (const ev of evidencias) {
    const tom = humanizarEvento(ev.event_type, ev.payload).tom;
    if (PESO_TOM[tom] > PESO_TOM[pior]) pior = tom;
  }
  return pior;
}

/**
 * Ordena evidências de uma conta pelo que mais justifica um ban.
 *
 * Por GRAVIDADE, não por volume — a mesma escolha que `resumirIncidente` faz e
 * pela mesma razão: 1 fmod.dll trojanizada decide um ban; 40 varreduras de
 * handle não decidem nada. Ordenar por contagem enterraria a prova embaixo do
 * ruído. Desempate por severidade e depois por repetição.
 */
export function ordenarEvidencias<T extends EventoDeEvidencia>(evs: readonly T[]): T[] {
  return [...evs].sort((a, b) => {
    const ta = PESO_TOM[humanizarEvento(a.event_type, a.payload).tom];
    const tb = PESO_TOM[humanizarEvento(b.event_type, b.payload).tom];
    if (ta !== tb) return tb - ta;
    const sa = a.severity ?? 0;
    const sb = b.severity ?? 0;
    if (sa !== sb) return sb - sa;
    return (b.occurrence_count ?? 1) - (a.occurrence_count ?? 1);
  });
}

// ============================================================
// ORDEM DA FILA — ESPELHA a ordem do backend, NÃO inventa outra
// ============================================================
// A fila já chega ordenada por `banqueue::Build` (BanQueuePolicy.h §ORDENAÇÃO).
// Reordenar aqui por uma escala PRÓPRIA criaria uma segunda verdade sobre
// gravidade — o erro que este projeto já pagou caro (D3) e que o próprio
// backend cita como razão de classificar tier em C++ e não em SQL.
//
// Então por que reordenar? Porque a ordem é o que decide quem é olhado antes
// de o operador cansar, e ela não pode depender de a query do dia ter mantido
// o ORDER BY, nem de um filtro client-side ter embaralhado a lista. Os
// critérios abaixo são TRANSCRIÇÃO dos do backend, na mesma sequência.
//
// ⚠️ UMA DIVERGÊNCIA DELIBERADA, e está escrita: `active_sanction` empurra a
// linha para o FIM. O backend não faz isso — ele anexa a sanção ativa como
// informação. Não é discordância sobre gravidade: é que não há mais decisão a
// tomar ali, e ocupar o topo de uma fila de revisão com trabalho já feito é
// exatamente como uma fila de revisão morre.

/** Peso do tier, como o backend os serializa em `top_tier` / `tier`. */
const PESO_TIER: Record<string, number> = {
  confirmed: 4,
  strong: 3,
  contextual: 2,
  ignore: 1,
};

export function pesoDoTier(tier: string | undefined): number {
  return PESO_TIER[tier ?? ''] ?? 0;
}

/** Linha da fila, no que a ordenação precisa saber dela. */
export interface LinhaOrdenavel {
  target_value: string;
  top_tier?: string;
  kick_eligible?: boolean;
  families?: { confirmed: number; strong: number; contextual: number };
  evidence_score?: number;
  last_seen?: string;
  active_sanction?: unknown;
}

function instante(iso: string | undefined): number {
  if (!iso) return 0;
  const t = new Date(iso).getTime();
  return Number.isNaN(t) ? 0 : t;
}

/**
 * Chave de ordenação, do critério mais decisivo para o menos.
 * Ordem idêntica à documentada em BanQueuePolicy.h, com o item 0 acrescentado.
 */
export function chaveDeGravidade(linha: LinhaOrdenavel): number[] {
  return [
    linha.active_sanction ? 0 : 1,          // 0. divergência declarada
    pesoDoTier(linha.top_tier),             // 1. Confirmed antes de Strong
    linha.kick_eligible ? 1 : 0,            // 2. o que sustenta expulsão
    linha.families?.confirmed ?? 0,         // 3. mais causas confirmadas
    linha.families?.strong ?? 0,            // 4.
    linha.evidence_score ?? 0,              // 5.
    instante(linha.last_seen),              // 6. o que ainda acontece é urgente
  ];
}

/**
 * Desempate FINAL por `target_value`: sem ele, duas contas empatadas trocariam
 * de lugar entre um refresh e outro e o operador perderia o lugar na lista.
 */
export function compararGravidade(a: LinhaOrdenavel, b: LinhaOrdenavel): number {
  const ka = chaveDeGravidade(a);
  const kb = chaveDeGravidade(b);
  for (let i = 0; i < ka.length; i++) {
    if (ka[i] !== kb[i]) return kb[i] - ka[i];
  }
  return a.target_value.localeCompare(b.target_value);
}

/** Mais grave primeiro. Não muta a lista de entrada. */
export function ordenarFilaDeBan<T extends LinhaOrdenavel>(linhas: readonly T[]): T[] {
  return [...linhas].sort(compararGravidade);
}

/** Rótulo do tier para o operador — a mesma escala que o motor de risco usa. */
export function rotuloDoTier(tier: string | undefined): string {
  switch (tier) {
    case 'confirmed':  return 'alteração confirmada';
    case 'strong':     return 'sinal forte';
    case 'contextual': return 'precisa de contexto';
    case 'ignore':     return 'não é sobre o jogador';
    default:           return tier ?? '—';
  }
}

/** Classes Tailwind do tier — mesma paleta que `corDoTom` usa nas outras telas. */
export function corDoTier(tier: string | undefined): string {
  switch (tier) {
    case 'confirmed':  return 'text-red-300 border-red-500/40 bg-red-500/10';
    case 'strong':     return 'text-orange-300 border-orange-500/40 bg-orange-500/10';
    case 'contextual': return 'text-yellow-200 border-yellow-500/30 bg-yellow-500/10';
    default:           return 'text-gray-400 border-gray-600/40 bg-gray-600/10';
  }
}
